import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useLang, translations } from "../i18n";

const transactions = [
  { id: "dep_7f3a", amount: "R$ 250.00", sats: "45,750", vFee: "125", spread: "688", revenue: "688", status: "completed" as const },
  { id: "dep_9b1c", amount: "R$ 1,000.00", sats: "183,020", vFee: "500", spread: "2,780", revenue: "2,780", status: "completed" as const },
  { id: "dep_3e8d", amount: "R$ 500.00", sats: "91,510", vFee: "250", spread: "1,390", revenue: "1,390", status: "completed" as const },
  { id: "dep_2a4f", amount: "R$ 100.00", sats: "18,302", vFee: "50", spread: "278", revenue: "—", status: "awaiting_payment" as const },
];

export function DashboardSection() {
  const { lang } = useLang();
  const t = translations.dashboard;
  const ref = useScrollReveal(0);

  return (
    <section className="bg-[var(--background-dark)] text-white py-20 md:py-28 lg:py-32 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: [
            "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px)",
            "linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)",
          ].join(", "),
          backgroundSize: "60px 60px",
        }}
      />
      <div ref={ref} className="max-w-6xl mx-auto relative z-10">
        <span className="reveal inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--primary)] px-3 py-1.5 bg-[var(--primary)]/10 border border-[var(--primary)]/20 rounded-full mb-4">
          {t.badge[lang]}
        </span>
        <h2 className="reveal text-3xl md:text-[40px] font-extrabold tracking-tight leading-[1.15] mb-3">
          {t.heading[lang]}
        </h2>
        <p className="reveal text-[15px] text-[var(--text-muted-dark)] mb-12 max-w-lg leading-relaxed">
          {t.description[lang]}
        </p>

        <div className="reveal bg-[var(--code-bg)] border border-white/[0.08] rounded-xl overflow-hidden shadow-2xl shadow-black/30">
          <div className="flex items-center gap-0 px-5 border-b border-white/[0.06] bg-white/[0.02]">
            <span className="px-4 py-3 text-[12px] font-medium text-white/30">{t.tabs.spread[lang]}</span>
            <span className="px-4 py-3 text-[12px] font-semibold text-[var(--primary)] border-b-2 border-[var(--primary)]">{t.tabs.transactions[lang]}</span>
            <span className="px-4 py-3 text-[12px] font-medium text-white/30">{t.tabs.revenue[lang]}</span>
            <span className="px-4 py-3 text-[12px] font-medium text-white/30">{t.tabs.wallet[lang]}</span>
            <span className="px-4 py-3 text-[12px] font-medium text-white/30">{t.tabs.apiKeys[lang]}</span>
          </div>

          <div className="px-5 py-4 border-b border-white/[0.06] bg-white/[0.01] flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-white/30 uppercase tracking-wider font-semibold">{t.spreadLabel[lang]}</span>
              <span className="font-mono text-sm text-[var(--primary)] font-bold">1.5%</span>
            </div>
            <div className="text-[12px] text-white/30 font-mono">
              R$ 100.00 → <span className="text-white/60">18,302 sats</span>
              <span className="text-white/20 mx-2">|</span>
              {t.yourRevenue[lang]}: <span className="text-[var(--primary)]">278 sats</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-white/[0.06] text-white/30 text-left">
                  <th className="px-5 py-3 font-semibold">{t.columns.id[lang]}</th>
                  <th className="px-5 py-3 font-semibold">{t.columns.amount[lang]}</th>
                  <th className="px-5 py-3 font-semibold">{t.columns.satsOut[lang]}</th>
                  <th className="px-5 py-3 font-semibold">{t.columns.vFee[lang]}</th>
                  <th className="px-5 py-3 font-semibold">{t.columns.spread[lang]}</th>
                  <th className="px-5 py-3 font-semibold">{t.columns.revenue[lang]}</th>
                  <th className="px-5 py-3 font-semibold">{t.columns.status[lang]}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-white/[0.04] last:border-0">
                    <td className="px-5 py-3 font-mono text-white/50">{tx.id}</td>
                    <td className="px-5 py-3 text-white/70">{tx.amount}</td>
                    <td className="px-5 py-3 font-mono text-white/70">{tx.sats}</td>
                    <td className="px-5 py-3 font-mono text-white/30">{tx.vFee}</td>
                    <td className="px-5 py-3 font-mono text-white/40">{tx.spread}</td>
                    <td className="px-5 py-3 font-mono text-[var(--primary)]">{tx.revenue}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider ${tx.status === "completed" ? "text-emerald-400" : "text-amber-400"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${tx.status === "completed" ? "bg-emerald-400" : "bg-amber-400"}`} />
                        {tx.status === "completed" ? t.completed[lang] : t.awaitingPix[lang]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
